/**
 * createFormDeriveEngineConfig
 *
 * Distills a FormType's authored derive configuration into the program the
 * engine runs. Validate-don't-trust: a malformed entry is DROPPED, never
 * repaired, and the survivors keep config order — the order the reconciler's
 * tie-break and every stamp depend on. The parameter is structural and types
 * `formDerives` as `unknown` — the honest signature for a validator, and the one
 * that lets a server caller's raw `doc.data()`, a `FormType` and a deeply
 * readonly Valtio snapshot of one all arrive with no cast at any call site.
 * The four rules with teeth: an algebra variable outside the alias set would bind
 * `undefined` and coerce to a silent `0`; two entries on one target are two
 * reconciling writers on one location, which the cycle graph cannot see; a DOTTED
 * typeId is accepted in the two positions that address a composite sub-field
 * through `arFormulaTargetFieldSegments`' own split — a projection's declared
 * column, which writes there, and a column arm's `sourceCell`, which reads back
 * from there (see `isColumnPathTypeId`) — and rejected in the four flat source
 * positions (see `isColumnTypeId`); and a projection guard may read only an alias
 * a strictly EARLIER column of the same entry declared, which is what lets the
 * cycle graph give guard reads no node at all.
 * One further refusal runs AFTER the fold rather than inside it, because it is a
 * property of the surviving set and not of any single entry:
 * `formDeriveChainedReferenceHopIds` names the projections that would read a
 * record through a table another projection fills, and this drops them. That
 * module owns the reasoning and is the only spelling of the rule.
 */

import {
  formulaConstantNames,
  formulaFunctionNames,
  formulaOperatorNames,
  parseFormula,
} from '../arTableFormulaEngine';
import { formDeriveChainedReferenceHopIds } from './formDeriveChainedReferenceHopIds';
import { formDeriveTargetKey } from './formDeriveTargetKey';
import { isColumnPathTypeId } from './isColumnPathTypeId';
import { isColumnTypeId } from './isColumnTypeId';
import { isRecordValue } from './isRecordValue';
import type { ArColumnAggregation } from '../System.Artifacts.types';
import type {
  FormDeriveEntry,
  FormDeriveFieldTarget,
  FormDeriveOperand,
  FormDeriveReferenceProp,
  FormDeriveScalarSource,
  FormDeriveTableTarget,
} from '../System.FormDerive.types';

/** Validated entries in config order, plus the rev every stamp travels with. */
export type FormDeriveEngineConfig = {
  entries: FormDeriveEntry[];
  rev: number;
};

/** docId-safe and free of `~`, so `drv~<deriveId>~<suffix>` stays parseable. */
const DERIVE_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

const ALIAS_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

/**
 * Every name the evaluator already resolves — grammar functions, constants AND
 * named operators — all three read off the live parser, so none can drift. The
 * two beyond functions are the live hazards, because both slip past the
 * `variables ⊆ aliases` check below: `Expression.variables()` filters only
 * against `functions`, so `true` (a constant) and `length` (a unary operator)
 * both tokenize as something other than a variable, and the operand aliased to
 * either is never read at evaluation.
 */
const reservedAliasNames = new Set<string>([
  ...formulaFunctionNames,
  ...formulaConstantNames,
  ...formulaOperatorNames,
]);

/** Exhaustive by construction: a new `ArColumnAggregation` member fails to compile here. */
const columnAggregations: Record<ArColumnAggregation, true> = {
  sum: true,
  avg: true,
  min: true,
  max: true,
  count: true,
};

function isColumnAggregation(value: unknown): value is ArColumnAggregation {
  return typeof value === 'string' && Object.keys(columnAggregations).includes(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

function isOptionalBoolean(value: unknown): boolean {
  return value === undefined || typeof value === 'boolean';
}

function isReferenceProp(value: unknown): value is FormDeriveReferenceProp {
  if (!isRecordValue(value)) return false;
  if (value.kind === 'name') return true;
  if (value.kind === 'field') return isNonEmptyString(value.fieldId);
  return value.kind === 'reference' && isNonEmptyString(value.referenceKey);
}

function isScalarSource(value: unknown): value is FormDeriveScalarSource {
  if (!isRecordValue(value)) return false;
  if (value.kind === 'field') return isNonEmptyString(value.fieldId);
  if (value.kind === 'colAggregate') {
    return (
      isNonEmptyString(value.tableId) &&
      isColumnTypeId(value.typeId) &&
      isColumnAggregation(value.aggregation) &&
      (value.aggregationFieldPath === undefined ||
        typeof value.aggregationFieldPath === 'string')
    );
  }
  return (
    value.kind === 'referenceProp' &&
    isNonEmptyString(value.referenceKey) &&
    isReferenceProp(value.prop)
  );
}

/**
 * A table target must NOT carry a `fieldId`: `'fieldId' in entry.target` is the
 * system-wide narrowing, so one stray member would make the whole engine read a
 * projection as a field write. It must not carry a `typeId` either — that is the
 * retired col target, which `formDeriveTargetKey` answers `undefined` for, and an
 * entry keyed `undefined` would escape the duplicate-target dedupe.
 */
function isTableTarget(value: unknown): value is FormDeriveTableTarget {
  return (
    isRecordValue(value) &&
    !('fieldId' in value) &&
    isNonEmptyString(value.tableId) &&
    value.typeId === undefined
  );
}

function isFieldTarget(value: unknown): value is FormDeriveFieldTarget {
  return isRecordValue(value) && isNonEmptyString(value.fieldId);
}

function isOperand(value: unknown): value is FormDeriveOperand {
  return (
    isRecordValue(value) &&
    typeof value.alias === 'string' &&
    ALIAS_PATTERN.test(value.alias) &&
    isScalarSource(value.source)
  );
}

/** `undefined` when the expression does not parse; the entry is then dropped. */
function readExpressionVariables(expression: string): string[] | undefined {
  try {
    return parseFormula(expression).variables;
  } catch {
    return undefined;
  }
}

function isAlgebraicEntry(value: Record<string, unknown>): boolean {
  const operands: unknown[] = Array.isArray(value.operands) ? value.operands : [];
  if (operands.length === 0) return false;
  const validated = operands.flatMap((operand) => (isOperand(operand) ? [operand] : []));
  if (validated.length !== operands.length) return false;

  const aliases = validated.map(({ alias }) => alias);
  if (new Set(aliases).size !== aliases.length) return false;
  if (aliases.some((alias) => reservedAliasNames.has(alias))) return false;

  if (!isNonEmptyString(value.expression)) return false;
  const variables = readExpressionVariables(value.expression);
  if (variables === undefined) return false;
  const aliasSet = new Set(aliases);
  if (variables.some((variable) => !aliasSet.has(variable))) return false;

  return isFieldTarget(value.target) && isOptionalBoolean(value.roundToCents);
}

function isCaseLiteral(value: unknown): boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** An alias reference, the object wrapper that tells `{ alias: 'x' }` from `'x'`. */
function isDeclaredAliasOperand(value: unknown, declaredAliases: ReadonlySet<string>): boolean {
  return isRecordValue(value) && typeof value.alias === 'string' && declaredAliases.has(value.alias);
}

/**
 * `left` and any alias-shaped `right` must name a column declared EARLIER in
 * this entry — the rule that keeps the fold well-founded and, with it, the
 * cycle graph's licence to give guard reads no node. An ordering operator takes
 * a numeric or alias right operand only.
 */
function isCaseCondition(value: unknown, declaredAliases: ReadonlySet<string>): boolean {
  if (!isRecordValue(value)) return false;
  if (typeof value.left !== 'string' || !declaredAliases.has(value.left)) return false;
  if (value.op === '==' || value.op === '!=') {
    return isCaseLiteral(value.right) || isDeclaredAliasOperand(value.right, declaredAliases);
  }
  if (value.op !== '<' && value.op !== '<=' && value.op !== '>' && value.op !== '>=') return false;
  return typeof value.right === 'number' || isDeclaredAliasOperand(value.right, declaredAliases);
}

function isCase(value: unknown, declaredAliases: ReadonlySet<string>): boolean {
  if (!isRecordValue(value)) return false;
  if (value.value !== null && !isCaseLiteral(value.value)) return false;
  if (value.when === undefined) return true;
  return (
    Array.isArray(value.when) &&
    value.when.every((condition) => isCaseCondition(condition, declaredAliases))
  );
}

/**
 * One column's value arm. Every typeId here reads the entry's source table.
 * `sourceCell` alone may address a composite sub-field, because
 * `readFormDeriveCellValue` resolves `parent.sub` to the same
 * `value.fields.<sub>.value` a dotted column writes; `groupAggregate` and
 * `rowReferenceProp` fold or hop off whole cells and stay undotted.
 * `roundToCents` lives on `groupAggregate` alone, pinned `never` on the other
 * four, so a persisted flag elsewhere drops the entry rather than claiming a
 * rounding the engine ignores.
 */
function isRowValueSource(value: unknown, declaredAliases: ReadonlySet<string>): boolean {
  if (!isRecordValue(value)) return false;
  if (value.kind === 'groupKey') return value.roundToCents === undefined;
  if (value.kind === 'sourceCell') {
    return isColumnPathTypeId(value.typeId) && value.roundToCents === undefined;
  }
  if (value.kind === 'groupAggregate') {
    return (
      isColumnTypeId(value.typeId) &&
      isColumnAggregation(value.aggregation) &&
      (value.aggregationFieldPath === undefined ||
        typeof value.aggregationFieldPath === 'string') &&
      isOptionalBoolean(value.roundToCents)
    );
  }
  if (value.kind === 'rowReferenceProp') {
    return (
      isColumnTypeId(value.referenceTypeId) &&
      isReferenceProp(value.prop) &&
      value.roundToCents === undefined
    );
  }
  return (
    value.kind === 'cases' &&
    value.roundToCents === undefined &&
    Array.isArray(value.cases) &&
    value.cases.every((entry) => isCase(entry, declaredAliases))
  );
}

/**
 * Left to right, because an alias binds only for LATER columns: the alias set
 * grows after the column's own value is validated, so a guard can never read the
 * column it guards. Two target rules beyond uniqueness: a dotted typeId is legal
 * here and, among the source positions, in `sourceCell` alone; and a plain column
 * may not sit alongside a dotted one whose root it is — `parent` beside
 * `parent.sub` is two writers on one cell, which `cellNodeKey`'s root collapse
 * would then hide.
 */
function areProjectionColumnsValid(columns: readonly unknown[]): boolean {
  const declaredAliases = new Set<string>();
  const typeIds = new Set<string>();

  for (const column of columns) {
    if (!isRecordValue(column)) return false;
    if (!isColumnPathTypeId(column.typeId) || typeIds.has(column.typeId)) return false;
    if (!isRowValueSource(column.value, declaredAliases)) return false;
    if (column.alias !== undefined) {
      if (typeof column.alias !== 'string' || !ALIAS_PATTERN.test(column.alias)) return false;
      if (reservedAliasNames.has(column.alias) || declaredAliases.has(column.alias)) return false;
      declaredAliases.add(column.alias);
    }
    typeIds.add(column.typeId);
  }

  const dottedRoots = [...typeIds].flatMap((typeId) => {
    const dotIndex = typeId.indexOf('.');
    return dotIndex === -1 ? [] : [typeId.slice(0, dotIndex)];
  });
  return ![...typeIds].some((typeId) => dottedRoots.includes(typeId));
}

/**
 * A self-projection — one table both partitioned and written — is refused here
 * rather than left for the cycle detector, which would report it as a loop the
 * author cannot act on. `roundToCents` is per column on this arm, so the
 * entry-level flag must be absent.
 */
function isProjectionEntry(value: Record<string, unknown>): boolean {
  const { source, target } = value;
  if (!isRecordValue(source) || source.kind !== 'colUniques') return false;
  if (!isNonEmptyString(source.tableId) || !isColumnTypeId(source.typeId)) return false;
  if (!isTableTarget(target) || target.tableId === source.tableId) return false;
  if (value.roundToCents !== undefined) return false;
  const columns: unknown[] = Array.isArray(value.columns) ? value.columns : [];
  return columns.length > 0 && areProjectionColumnsValid(columns);
}

/**
 * `roundToCents` is pinned to `never` on the arms that cannot produce a
 * fractional number, so a persisted flag there is a shape mismatch, not noise —
 * dropping it is what keeps the settings UI from claiming a rounding the engine
 * would ignore.
 */
function isSingleSourceEntry(value: Record<string, unknown>): boolean {
  const { source } = value;
  if (!isScalarSource(source) || !isFieldTarget(value.target)) return false;
  return source.kind === 'colAggregate'
    ? isOptionalBoolean(value.roundToCents)
    : value.roundToCents === undefined;
}

/**
 * Both refresh parameters are optional, and each is a finite number at or above
 * zero — zero being the authored spelling of "off", which the floor at
 * `FORM_DERIVE_REFRESH_MIN_MS` and each reader's own `> 0` test then honor. A
 * NaN, an Infinity or a negative window would make `Math.max` against that floor
 * answer nonsense, and a string would compare as one, so a malformed value drops
 * the entry here rather than reaching a reader that only optional-chains.
 */
function areRefreshTriggersValid(value: unknown): boolean {
  if (value === undefined) return true;
  if (!isRecordValue(value)) return false;
  const parameters = [value.onChangeDebounceMs, value.whileOpenIntervalMinutes];
  return parameters.every(
    (parameter) =>
      parameter === undefined ||
      (typeof parameter === 'number' && Number.isFinite(parameter) && parameter >= 0),
  );
}

function isFormDeriveEntry(value: unknown): value is FormDeriveEntry {
  if (!isRecordValue(value)) return false;
  if (!isNonEmptyString(value.deriveId) || !DERIVE_ID_PATTERN.test(value.deriveId)) return false;
  if (!areRefreshTriggersValid(value.refresh)) return false;
  if ('operands' in value) return isAlgebraicEntry(value);
  return 'columns' in value ? isProjectionEntry(value) : isSingleSourceEntry(value);
}

function createFormDeriveEngineConfig(
  formType: { formDerives?: unknown; formDeriveRev?: number } | undefined,
): FormDeriveEngineConfig {
  const configured: unknown = formType?.formDerives;
  const candidates: unknown[] = Array.isArray(configured) ? configured : [];

  const seenDeriveIds = new Set<string>();
  const seenTargets = new Set<string>();

  const deduped = candidates.flatMap((candidate) => {
    if (!isFormDeriveEntry(candidate)) return [];
    if (seenDeriveIds.has(candidate.deriveId)) return [];
    const target = formDeriveTargetKey(candidate.target);
    if (seenTargets.has(target)) return [];
    seenDeriveIds.add(candidate.deriveId);
    seenTargets.add(target);
    return [candidate];
  });

  const chainedHops = formDeriveChainedReferenceHopIds(deduped);
  const entries = deduped.filter(({ deriveId }) => !chainedHops.has(deriveId));

  return { entries, rev: formType?.formDeriveRev ?? 0 };
}

export { createFormDeriveEngineConfig };
